import React, { useEffect, useState } from "react";
import { getCurrentServerInstant, getServerTimezone } from "@/shared/utils/serverTimeContext";
import Tooltip from "@/shared/components/Tooltip";

/**
 * Displays the current time in the authoritative server timezone using the
 * bootstrapped server instant when available, then advances locally from that
 * baseline. Mirrors RoR header intent without relying on browser local time.
 */
const timeFormatterCache = new Map<string, Intl.DateTimeFormat>();
const tooltipFormatterCache = new Map<string, Intl.DateTimeFormat>();
const compactTimeFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getTimeFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = timeFormatterCache.get(timeZone);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
  });
  timeFormatterCache.set(timeZone, formatter);
  return formatter;
};

/**
 * Compact formatter — shows only HH:mm (no weekday, date, or timezone).
 * Used on small/medium screens to save navbar space for the search bar.
 * Matches the phase2_develop behaviour: just the clock digits in the pill.
 */
const getCompactTimeFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = compactTimeFormatterCache.get(timeZone);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  compactTimeFormatterCache.set(timeZone, formatter);
  return formatter;
};

const getTooltipFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = tooltipFormatterCache.get(timeZone);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
  tooltipFormatterCache.set(timeZone, formatter);
  return formatter;
};

const getFormattedServerTime = (): string => {
  const formatter = getTimeFormatter(getServerTimezone());
  const parts = formatter.formatToParts(getCurrentServerInstant());
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "00";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  const zone = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  return `${weekday} ${day} ${month} ${hour}:${minute} ${zone}`.trim();
};

const getCompactServerTime = (): string => {
  return getCompactTimeFormatter(getServerTimezone()).format(getCurrentServerInstant());
};

const getServerTooltipText = (timeZone: string): string => {
  const tooltipDateTime = getTooltipFormatter(timeZone).format(getCurrentServerInstant());
  return `Server time: ${tooltipDateTime} (${timeZone}). All time-based filters use server time.`;
};

const CurrentTime: React.FC = () => {
  const [currentTime, setCurrentTime] = useState<string>(getFormattedServerTime);
  const [compactTime, setCompactTime] = useState<string>(getCompactServerTime);
  const [serverTimezone, setServerTimezone] = useState<string>(getServerTimezone);

  useEffect(() => {
    const tick = () => {
      setCurrentTime((prev) => {
        const next = getFormattedServerTime();
        return prev === next ? prev : next;
      });
      setCompactTime((prev) => {
        const next = getCompactServerTime();
        return prev === next ? prev : next;
      });
      setServerTimezone((prev) => {
        const next = getServerTimezone();
        return prev === next ? prev : next;
      });
    };

    tick();
    const interval = setInterval(tick, 10_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Tooltip
      text={getServerTooltipText(serverTimezone)}
      position="bottom"
      align="center"
    >
      {/*
       * Figma: 148×27px, radius 51px, padding 6px 12px, gap 10px, bg #000000.
       * Responsive: on screens < lg show only HH:mm (compact) to free up
       * space for the search bar — mirrors phase2_develop behaviour.
       * On lg+ show the full weekday/date/timezone string.
       */}
      <span
        className="text-white text-xs font-normal leading-none tracking-normal whitespace-nowrap inline-flex items-center justify-center gap-[10px] rounded-[51px] bg-black px-3 py-[6px]"
        style={{ fontFamily: "Inter, sans-serif", letterSpacing: 0 }}
        data-testid="navbar-current-time"
        aria-live="off"
      >
        {/* Compact: HH:mm only — visible below lg breakpoint. Hidden from
            AT because the sr-only span below carries the full label. */}
        <span className="lg:hidden tabular-nums" aria-hidden={true}>{compactTime}</span>
        {/* Full: weekday day mon HH:mm TZ — visible at lg and above */}
        <span className="hidden lg:inline">{currentTime}</span>
        {/* Screen-reader full label at every viewport so AT users get
            weekday / month / timezone regardless of breakpoint. */}
        <span className="sr-only">{currentTime}</span>
      </span>
    </Tooltip>
  );
};

export default CurrentTime;
